import { DISTINCT_CREATIVES_METRIC_NAME } from '../../creatives/data/displayedMetrics';
import type { ElementLeafRow, ElementsTableData, MetricCatalog } from './api';

export type Scene = 'all' | 'hook-scene' | 'end-card';

const SCENE_PREFIXES: ReadonlyArray<{ scene: 'hook-scene' | 'end-card'; prefix: string }> = [
  { scene: 'hook-scene', prefix: 'Hook Scene ' },
  { scene: 'end-card', prefix: 'End Card ' },
];

export interface ElementTreeMetric {
  metricId: string;
  value: number | null;
}

export interface ElementTreeNode {
  level: 'category' | 'featureCategory' | 'feature' | 'tag';
  path: string[];
  name: string;
  metrics: ElementTreeMetric[];
}

interface BuildElementTreeOptions {
  activeScenes: readonly Scene[];
  catalog: MetricCatalog;
  metricIds: string[];
}

const LEVELS: ElementTreeNode['level'][] = ['category', 'featureCategory', 'feature', 'tag'];

export const TOTALS_PATH = ['__totals__'];

export type ElementsTotals = ElementTreeNode;

export function sceneOf(feature: string): Scene {
  const match = SCENE_PREFIXES.find((entry) => feature.startsWith(entry.prefix));
  return match?.scene ?? 'all';
}

export function filterByScene(
  rows: ElementLeafRow[],
  activeScenes: readonly Scene[],
): ElementLeafRow[] {
  if (activeScenes.length === 0) return rows;
  return rows.filter((row) => activeScenes.includes(sceneOf(row.feature)));
}

function metricsFrom(row: ElementLeafRow, metricIds: string[]): ElementTreeMetric[] {
  return metricIds.map((metricId) => ({
    metricId,
    // creativesCount is the row's own authoritative distinct-creative count — read that instead
    // of the flat metrics record's countd_assets entry, which needs it in metricIds to exist at all.
    value:
      metricId === DISTINCT_CREATIVES_METRIC_NAME
        ? row.creativesCount
        : (row.metrics[metricId] ?? null),
  }));
}

function aggregateMetric(
  members: ElementLeafRow[],
  metricId: string,
  shape: MetricCatalog[string]['shape'] | undefined,
): number | null {
  const values = members
    .map((row) => row.metrics[metricId] ?? null)
    .filter((value): value is number => value !== null);
  if (values.length === 0) return null;
  if (shape === 'ratio') return null;
  if (shape === 'count') return values.length === 1 ? (values[0] as number) : null;
  return values.reduce((sum, value) => sum + value, 0);
}

function groupBy<T>(items: T[], keyOf: (item: T) => string): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return groups;
}

function aggregateGroupNode(
  path: string[],
  members: ElementLeafRow[],
  options: BuildElementTreeOptions,
): ElementTreeNode {
  return {
    level: LEVELS[path.length - 1] as ElementTreeNode['level'],
    path,
    name: path[path.length - 1] as string,
    metrics: options.metricIds.map((metricId) => ({
      metricId,
      value: aggregateMetric(members, metricId, options.catalog[metricId]?.shape),
    })),
  };
}

function groupNodes(
  rows: ElementLeafRow[],
  pathOf: (row: ElementLeafRow) => string[],
  options: BuildElementTreeOptions,
): ElementTreeNode[] {
  const groups = groupBy(rows, (row) => JSON.stringify(pathOf(row)));
  return [...groups.entries()].map(([key, members]) =>
    aggregateGroupNode(JSON.parse(key) as string[], members, options),
  );
}

export function buildElementTree(
  rows: ElementLeafRow[],
  options: BuildElementTreeOptions,
): ElementTreeNode[] {
  const scoped = filterByScene(rows, options.activeScenes);
  const featureRows = scoped.filter((row) => row.rowLevel === 'feature');
  const tagRows = scoped.filter((row) => row.rowLevel === 'tag');

  const categoryNodes = groupNodes(featureRows, (row) => [row.category], options);
  const featureCategoryNodes = groupNodes(
    featureRows,
    (row) => [row.category, row.featureCategory],
    options,
  );
  const featureNodes = featureRows.map((row) => ({
    level: 'feature' as const,
    path: [row.category, row.featureCategory, row.feature],
    name: row.feature,
    metrics: metricsFrom(row, options.metricIds),
  }));
  const tagNodes = tagRows.map((row) => ({
    level: 'tag' as const,
    path: [row.category, row.featureCategory, row.feature, row.tag],
    name: row.tag,
    metrics: metricsFrom(row, options.metricIds),
  }));

  return [...categoryNodes, ...featureCategoryNodes, ...featureNodes, ...tagNodes];
}

export function buildElementsTotals(
  total: ElementsTableData['total'] | undefined,
  metricIds: string[],
): ElementsTotals {
  const creativesCount = total?.creativesCount ?? 0;
  return {
    level: 'category',
    path: TOTALS_PATH,
    name: `All elements · ${creativesCount}`,
    metrics: metricIds.map((metricId) => ({
      metricId,
      value: total?.metrics[metricId] ?? null,
    })),
  };
}

export function pathKey(path: readonly string[]): string {
  return JSON.stringify(path);
}

export function parentPathKeys(nodes: readonly ElementTreeNode[]): Set<string> {
  return new Set(
    nodes.filter((node) => node.path.length > 1).map((node) => pathKey(node.path.slice(0, -1))),
  );
}

export function visibleTreeRows(
  nodes: readonly ElementTreeNode[],
  expandedPaths: ReadonlySet<string>,
  compareSiblings?: (a: ElementTreeNode, b: ElementTreeNode) => number,
): ElementTreeNode[] {
  const childrenByParent = new Map<string, ElementTreeNode[]>();
  for (const node of nodes) {
    const parent = pathKey(node.path.slice(0, -1));
    const siblings = childrenByParent.get(parent);
    if (siblings === undefined) childrenByParent.set(parent, [node]);
    else siblings.push(node);
  }

  const rows: ElementTreeNode[] = [];
  const walk = (parent: string): void => {
    const children = childrenByParent.get(parent);
    if (children === undefined) return;
    const ordered = compareSiblings === undefined ? children : [...children].sort(compareSiblings);
    for (const child of ordered) {
      rows.push(child);
      const key = pathKey(child.path);
      if (expandedPaths.has(key)) walk(key);
    }
  };
  walk(pathKey([]));
  return rows;
}
